import type { Patient, Reference } from '@medplum/fhirtypes'
import { Desk } from '@seen/core/models/desk'
import { Button, TypographySubtle } from '@seen/ui'
import { IconBuilding, IconEye } from '@tabler/icons-react'
import type { ReactFlowJsonObject } from '@xyflow/react'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export function DeskComposerDialogContent({
  desk,
  onCreate,
  onUpdate,
  subject,
}: {
  desk?: Desk
  onCreate?: (desk: Desk) => void
  onUpdate?: (desk: Desk) => void
  subject?: Reference<Patient>
}): ReactNode {
  const createMutation = useCreateMutation<Desk>()
  const patchMutation = usePatchMutation<Desk>()
  const currentPractitioner = useCurrentPractitioner()

  const form = useForm({
    schema: z.object({
      title: z.string().min(1),
      visibility: z.enum(['private', 'shared']),
    }),
    defaultValues: {
      title: desk?.title ?? '',
      visibility:
        (desk?.access ?? 'shared') === 'shared' ? 'shared' : 'private',
    },
  })

  const submit = form.handleSubmit((values) => {
    if (desk) {
      patchMutation.mutate(
        {
          instance: desk,
          mutateFn: (d) => {
            d.title = values.title
            d.access =
              values.visibility === 'shared'
                ? 'shared'
                : currentPractitioner.reference
          },
        },
        {
          onSuccess: (updatedDesk) => {
            onUpdate?.(updatedDesk)
          },
        }
      )
    } else {
      createMutation.mutate(
        new Desk({
          flowObject: NEW_DESK,
          author: currentPractitioner.reference,
          title: values.title,
          access:
            values.visibility === 'shared'
              ? 'shared'
              : currentPractitioner.reference,
          subject,
        }),
        {
          onSuccess: (createdDesk) => {
            onCreate?.(createdDesk)
          },
        }
      )
    }
  })

  return (
    <Form onSubmit={submit}>
      <DialogHeader className="py-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <RichTextEditor
              placeholder="Desk name"
              variant="unstyled"
              className="flex-1"
              contentClassName="text-xl font-bold"
              {...field}
            />
          )}
        />
      </DialogHeader>
      <DialogFooter>
        <FormField
          control={form.control}
          name="visibility"
          render={({ field: { value, onChange, ...rest } }) => (
            <div className="flex items-center gap-2">
              <TypographySubtle className="text-foreground-secondary">
                Save to
              </TypographySubtle>
              <Combobox
                options={[
                  {
                    label: (
                      <ComboboxOption isSelected={value === 'private'}>
                        <IconEye className="size-4 shrink-0" />
                        Private
                      </ComboboxOption>
                    ),
                    value: 'private',
                    keywords: ['Private'],
                  },
                  {
                    label: (
                      <ComboboxOption isSelected={value === 'shared'}>
                        <IconBuilding className="size-4 shrink-0" />
                        Organization
                      </ComboboxOption>
                    ),
                    value: 'shared',
                    keywords: ['Shared'],
                  },
                ]}
                onSelect={onChange}
              >
                <Button variant="outline" className="gap-2" {...rest}>
                  {value === 'private' && (
                    <>
                      <IconEye className="size-4 shrink-0" />
                      Private
                    </>
                  )}
                  {value === 'shared' && (
                    <>
                      <IconBuilding className="size-4 shrink-0" />
                      Organization
                    </>
                  )}
                </Button>
              </Combobox>
            </div>
          )}
        />
        <div className="my-1 w-[0.5px] self-stretch bg-border" />
        <DialogClose asChild>
          <Button variant="outline">Cancel</Button>
        </DialogClose>
        <SubmitButton
          submitting={createMutation.isPending || patchMutation.isPending}
        >
          {desk ? 'Update desk' : 'Create desk'}
        </SubmitButton>
      </DialogFooter>
    </Form>
  )
}

const NEW_DESK: ReactFlowJsonObject = {
  nodes: [],
  edges: [],
  viewport: {
    x: 0,
    y: 0,
    zoom: 1,
  },
}
